/**
 * @file index.jsx
 * @author Daniel Aguirre
 * @description Dashboard Bar Component
 */

// Dependencies
import React from 'react';
import { Bar } from 'react-chartjs-2';
import PropTypes from 'prop-types';
import EmptyChart from 'components/EmptyChart';
import DashboardTitle from '../DashboardTitle';
import DashboardLabel from '../DashboardLabel';

let elmChartIndex = null;
let wasClicked = false;
let wasHovered = false;
const isMobile = window.innerWidth < 768;

const DashboardBar = ({
  title,
  description,
  dataValues,
  displayLabels = true,
  stacked,
  yTitle,
  xTitle,
  onDetailClick,
  labelDescription,
  percentScale
}) => {
  const { labels, years } = dataValues;
  let { datasets } = dataValues;

  const isSingleDataset = datasets.length === 1 || stacked;

  const borderRadius = isSingleDataset ? 5 : 10;
  const barThickness = isSingleDataset ? 30 : 20;
  const maxBarThickness = isSingleDataset ? 30 : 10;
  let empty = true;

  datasets = datasets.map(({ label, data, backgroundColor }) => {
    if (data.length > 0) {
      empty = false;
    }
    return {
      label,
      data,
      backgroundColor,
      borderRadius,
      barThickness,
      maxBarThickness
    };
  });

  const data = {
    labels,
    datasets
  };

  const options = {
    layout: {
      padding: {
        right: 5
      }
    },
    maintainAspectRatio: false,
    scales: {
      x: {
        title: {
          display: !!xTitle,
          text: xTitle
        },
        stacked
      },
      x2: years
        ? {
            ticks: {
              callback: (_, index) => {
                const pastYear = years[index - 1];
                const year = years[index];
                const nextYear = years[index + 1];
                if (year && pastYear && nextYear) {
                  if (year !== pastYear || year !== nextYear) return year;
                }
                return '';
              }
            },
            grid: {
              borderColor: 'transparent',
              display: false
            }
          }
        : { display: false },
      y: {
        title: {
          display: !!yTitle,
          text: yTitle
        },
        stacked,
        min: 0,
        suggestedMax: percentScale ? 100 : 0,
        ticks: {
          stepSize: 25,
          precision: 0
        }
      }
    },
    interaction: {
      intersect: false,
      mode: 'nearest',
      axis: 'xy'
    },
    plugins: {
      tooltip: {
        external: () => {
          if (isMobile) {
            wasHovered = true;
          }
        }
      },
      legend: {
        display: false
      }
    }
  };

  const handleChartClick = (element) => {
    if (element.length && !isMobile) {
      if (onDetailClick) {
        onDetailClick(element[0].index, element[0].datasetIndex);
      }
    } else if (isMobile && wasHovered && !wasClicked) {
      wasClicked = true;
      if (onDetailClick) {
        onDetailClick(
          elmChartIndex.tooltip.dataPoints[0].dataIndex,
          elmChartIndex.tooltip.dataPoints[0].datasetIndex
        );
      }
    } else {
      elmChartIndex = null;
    }
  };

  React.useEffect(() => {
    return () => {
      wasClicked = false;
    };
  }, []);

  return (
    <div className="flex flex-wrap h-full cursor-pointer">
      <div className="h-1/4">
        <DashboardTitle title={title} description={description} />
        {!empty && (
          <div className="flex flex-wrap items-baseline">
            {labelDescription && (
              <span className="mr-5 text-baxterGray">{labelDescription}</span>
            )}
            {displayLabels &&
              datasets.map(({ label, backgroundColor }, index) => (
                <DashboardLabel
                  horizontal
                  key={index}
                  label={label}
                  color={backgroundColor}
                />
              ))}
          </div>
        )}
      </div>
      {!empty ? (
        <div className="w-full h-3/4">
          <Bar
            data={data}
            options={options}
            getElementAtEvent={handleChartClick}
          />
        </div>
      ) : (
        <EmptyChart />
      )}
    </div>
  );
};

/**
 * Component definition of proptypes.
 */
DashboardBar.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  dataValues: PropTypes.object,
  displayLabels: PropTypes.bool,
  stacked: PropTypes.bool,
  yTitle: PropTypes.string,
  xTitle: PropTypes.string,
  years: PropTypes.array,
  onDetailClick: PropTypes.func,
  labelDescription: PropTypes.string,
  percentScale: PropTypes.bool
};

export default DashboardBar;
